import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { getProductName } from '../lib/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalSearch({ currentPincode }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchProducts(query);
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query, currentPincode]);

  const searchProducts = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/products?search=${encodeURIComponent(searchQuery)}&pincode=${currentPincode}&page_size=8`
      );
      const products = response.data.products || response.data;
      setResults(products);
      setShowResults(true);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (productId) => {
    navigate(`/product/${productId}`);
    setQuery('');
    setShowResults(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
      setQuery('');
      setShowResults(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search products..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowResults(true)}
            className="pl-10 pr-10 rounded-full"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setResults([]);
                setShowResults(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </form>

      {/* Live Search Results */}
      {showResults && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-slate-200 max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="p-4 text-center text-slate-500">Searching...</div>
          ) : results.length > 0 ? (
            <>
              {results.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleResultClick(product.id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                >
                  <img
                    src={product.image}
                    alt={getProductName(product)}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-slate-900">
                      {getProductName(product)}
                    </p>
                    <p className="text-xs text-green-600 font-semibold">
                      {formatPrice(product.discount_price || product.price)}
                    </p>
                  </div>
                </button>
              ))}
              <button
                onClick={() => {
                  navigate(`/products?search=${encodeURIComponent(query)}`);
                  setShowResults(false);
                }}
                className="w-full p-3 text-center text-sm text-green-600 hover:bg-green-50 transition-colors font-medium"
              >
                View All Results →
              </button>
            </>
          ) : (
            <div className="p-4 text-center text-slate-500">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
